/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The Ops Console empty state: a 56 px icon tile, a title, one line and the primary action.
 * Replaces the large Coriolis illustrations of the empty lists.
 */

import * as React from "react";
import styled from "styled-components";
import { Plus } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";
import type { IconTileTone } from "@src/components/ui/IconTile/IconTile";
import { T9 } from "@src/components/ui/Lists/ListTable";

const Wrapper = styled.div<{ compact?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-grow: 1;
  gap: 4px;
  padding: ${props => (props.compact ? "32px 16px" : "64px 24px")};
`;
const Title = styled.div`
  margin-top: 16px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: ${T9.slate900};
`;
const Line = styled.div`
  max-width: 520px;
  font-size: 14px;
  line-height: 20px;
  color: ${T9.slate500};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
`;
export const PrimaryButton = styled.button<{ secondary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border-radius: 6px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  border: 1px solid ${props => (props.secondary ? T9.slate300 : "transparent")};
  background: ${props => (props.secondary ? "var(--t9-bg-surface)" : T9.brand600)};
  color: ${props => (props.secondary ? T9.slate700 : "white")};
  &:hover {
    background: ${props => (props.secondary ? T9.slate50 : T9.brand700)};
  }
  &:focus-visible {
    outline: 2px solid ${T9.accent500};
    outline-offset: 2px;
  }
`;

type Props = {
  icon: LucideIcon;
  tone?: IconTileTone;
  title: string;
  message?: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
  compact?: boolean;
};
const EmptyState = (props: Props) => (
  <Wrapper compact={props.compact} data-test-id="emptyState">
    <IconTile icon={props.icon} size={56} tone={props.tone} />
    <Title data-test-id="emptyState-title">{props.title}</Title>
    {props.message ? <Line data-test-id="emptyState-message">{props.message}</Line> : null}
    {props.actionLabel || props.secondaryLabel ? (
      <Actions>
        {props.secondaryLabel ? (
          <PrimaryButton type="button" secondary onClick={props.onSecondary}>
            {props.secondaryLabel}
          </PrimaryButton>
        ) : null}
        {props.actionLabel ? (
          <PrimaryButton type="button" onClick={props.onAction} data-test-id="emptyState-button">
            <Plus size={16} />
            {props.actionLabel}
          </PrimaryButton>
        ) : null}
      </Actions>
    ) : null}
  </Wrapper>
);

export default EmptyState;
